import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

const ARTICLES_COLLECTION = 'articles';

export const articlesService = {
  async getAllArticles() {
    try {
      const q = query(collection(db, ARTICLES_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const articles = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const withOrder = articles.filter(a => a.sortOrder !== undefined);
      const withoutOrder = articles.filter(a => a.sortOrder === undefined);
      if (withOrder.length > 0) {
        withOrder.sort((a, b) => a.sortOrder - b.sortOrder);
        return [...withOrder, ...withoutOrder];
      }
      return articles;
    } catch (error) {
      console.error('Error fetching articles:', error);
      throw error;
    }
  },

  async getPublishedArticles() {
    try {
      const q = query(collection(db, ARTICLES_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const articles = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(article => article.published);
      const withOrder = articles.filter(a => a.sortOrder !== undefined);
      const withoutOrder = articles.filter(a => a.sortOrder === undefined);
      if (withOrder.length > 0) {
        withOrder.sort((a, b) => a.sortOrder - b.sortOrder);
        return [...withOrder, ...withoutOrder];
      }
      return articles;
    } catch (error) {
      console.error('Error fetching published articles:', error);
      throw error;
    }
  },

  async getArticleById(id) {
    try {
      const docRef = doc(db, ARTICLES_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Article not found');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      throw error;
    }
  },

  async createArticle(articleData) {
    try {
      const docRef = await addDoc(collection(db, ARTICLES_COLLECTION), {
        ...articleData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating article:', error);
      throw error;
    }
  },

  async updateArticle(id, articleData) {
    try {
      const docRef = doc(db, ARTICLES_COLLECTION, id);
      await updateDoc(docRef, {
        ...articleData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating article:', error);
      throw error;
    }
  },

  async deleteArticle(id) {
    try {
      const docRef = doc(db, ARTICLES_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting article:', error);
      throw error;
    }
  },

  async uploadImage(file, path = 'articles') {
    try {
      const fileName = `${path}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      return {
        url: downloadURL,
        path: fileName
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  async updateArticlesOrder(orderedIds) {
    try {
      const batch = writeBatch(db);
      orderedIds.forEach((id, index) => {
        const docRef = doc(db, ARTICLES_COLLECTION, id);
        batch.update(docRef, { sortOrder: index, updatedAt: serverTimestamp() });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error updating articles order:', error);
      throw error;
    }
  },

  async deleteImage(imagePath) {
    try {
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }
};